import type { SavedTrip } from "./types";

/** Filters and ordering of "Viajes guardados" (design turno 6), kept pure so they can be tested. */

export type SavedFilter = "all" | "wallet" | `region:${string}`;
export type SavedSort = "recent" | "price" | "departure";

export const SORT_LABELS: Record<SavedSort, string> = { recent: "Guardado reciente", price: "Precio: menor a mayor", departure: "Salida más próxima" };

/** A saved trip "con wallet" already has money or an open request moving toward it. */
export function hasWallet(item: SavedTrip): boolean {
  return item.apartado !== null || item.request !== null;
}

export interface SavedFilterOption { id: SavedFilter; label: string; count: number }

/** "Todos · N", one chip per region present in the collection, then "Con wallet · N". */
export function filterOptions(items: SavedTrip[]): SavedFilterOption[] {
  const regions = new Map<string, { name: string; count: number }>();
  for (const item of items) {
    const region = item.trip.region;
    if (!region) continue;
    const current = regions.get(region.slug);
    regions.set(region.slug, { name: region.name, count: (current?.count ?? 0) + 1 });
  }
  const options: SavedFilterOption[] = [{ id: "all", label: "Todos", count: items.length }];
  for (const [slug, { name, count }] of [...regions.entries()].sort((left, right) => right[1].count - left[1].count || left[1].name.localeCompare(right[1].name))) {
    options.push({ id: `region:${slug}`, label: name, count });
  }
  const withWallet = items.filter(hasWallet).length;
  if (withWallet) options.push({ id: "wallet", label: "Con wallet", count: withWallet });
  return options;
}

export function applyFilter(items: SavedTrip[], filter: SavedFilter): SavedTrip[] {
  if (filter === "all") return items;
  if (filter === "wallet") return items.filter(hasWallet);
  const slug = filter.slice("region:".length);
  return items.filter((item) => item.trip.region?.slug === slug);
}

export function sortSaved(items: SavedTrip[], sort: SavedSort): SavedTrip[] {
  const copy = [...items];
  switch (sort) {
    case "price":
      return copy.sort((left, right) => (left.trip.priceFrom?.amount ?? Number.POSITIVE_INFINITY) - (right.trip.priceFrom?.amount ?? Number.POSITIVE_INFINITY));
    case "departure":
      return copy.sort((left, right) => (left.trip.nextDeparture ?? "9999").localeCompare(right.trip.nextDeparture ?? "9999"));
    default:
      return copy.sort((left, right) => right.savedAt.localeCompare(left.savedAt));
  }
}

/** "6 viajes · 4 destinos · último guardado hace 2 días" without the relative-time part. */
export function collectionSummary(items: SavedTrip[]): { trips: number; destinations: number; withWallet: number; lastSavedAt: string | null } {
  const destinations = new Set(items.map((item) => item.trip.destination?.id ?? item.trip.countries[0] ?? item.trip.id));
  return {
    trips: items.length,
    destinations: destinations.size,
    withWallet: items.filter(hasWallet).length,
    lastSavedAt: items.reduce<string | null>((latest, item) => (latest === null || item.savedAt > latest ? item.savedAt : latest), null)
  };
}

export const COMPARE_MAX = 3;

export interface CompareRow { label: string; values: string[] }

/** The rows of "Comparar viajes": only facts every card already carries. */
export function compareRows(items: SavedTrip[], format: { price(amount: number, currency: string): string; date(value: string | null): string }): CompareRow[] {
  const value = (fn: (item: SavedTrip) => string | null | undefined) => items.map((item) => fn(item) || "—");
  return [
    { label: "Duración", values: value((item) => item.trip.durationDays ? `${item.trip.durationDays} días${item.trip.durationNights ? ` / ${item.trip.durationNights} noches` : ""}` : null) },
    { label: "Desde · por persona", values: value((item) => item.trip.priceFrom ? format.price(item.trip.priceFrom.amount, item.trip.priceFrom.currency) : "Consultar") },
    { label: "Ruta", values: value((item) => item.trip.routeDisplay) },
    { label: "Ciudades", values: value((item) => (item.trip.cities.length ? String(new Set(item.trip.cities).size) : null)) },
    { label: "Llegadas", values: value((item) => item.trip.arrivalDays.join(" y ")) },
    { label: "Próxima salida", values: value((item) => (item.trip.nextDeparture ? format.date(item.trip.nextDeparture) : null)) },
    { label: "Salidas disponibles", values: value((item) => (item.trip.departureCount ? String(item.trip.departureCount) : null)) },
    { label: "Mínimo de pasajeros", values: value((item) => (item.trip.minimumPassengers ? String(item.trip.minimumPassengers) : null)) },
    { label: "Tipo de servicio", values: value((item) => item.trip.serviceType) },
    { label: "Destacados", values: value((item) => item.trip.highlights.slice(0, 3).join(" · ")) },
    { label: "Wallet", values: value((item) => (item.apartado ? `Apartado ${Math.round((item.apartado.progress ?? 0) * 100)}%` : item.request ? `Solicitud ${item.request.folio}` : "Sin wallet")) }
  ];
}
